<script setup lang="ts">
import mapboxgl, { Map as MapboxMap, Marker, Projection } from 'mapbox-gl'
import { onMounted, Ref, ref } from 'vue'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import * as turf from '@turf/turf'
import { roundTo } from '@/utils/Math'

export interface MapContainerProps {
  /**
   * The element id for where the map will be injected and referenced
   */
  container: string
  /**
   * Mapbox style - The style of the map being used
   * Customizable on Mapbox
   */
  style: string
  /**
   * Mapbox access token required for interactions / API calls with Mapbox
   */
  accessToken: string
  /**
   * The type of Map projection that is used for displaying the Map
   */
  projectionType: Projection
}

const props = defineProps<MapContainerProps>()
const emit = defineEmits(['update:distance', 'clear:complete'])
const query: Ref<string> = ref('')
let mapbox: MapboxMap
const markerRef: Ref<Marker | null> = ref(null)

const searchLocationFromPlaces = async () => {
  const response = await fetch(`https://places.googleapis.com/v1/places:searchText`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': 'AIzaSyDfPx_hIOW1xau59Bd3AvFZc5iwJypdaek',
      'X-Goog-FieldMask':
        'places.id,places.location,places.viewport,places.formattedAddress,places.types,places.displayName,places.adrFormatAddress',
    },
    body: JSON.stringify({ textQuery: query.value.toLowerCase() }),
  })

  if (response.status !== 200) {
    console.log('Error fetching places')
    return
  }

  const data: any = await response.json()

  const place = data.places[0]
  if (!place) {
    console.log('No place found')
    return
  }

  const placeLocation = {
    id: place.id,
    bbox: [
      place.viewport.low.longitude,
      place.viewport.low.latitude,
      place.viewport.high.longitude,
      place.viewport.high.latitude,
    ],
    center: [place.location.longitude, place.location.latitude],
    place_name: place.formattedAddress,
    place_type: place.types || [],
    text: place.displayName.text || '',
    address: place.adrFormatAddress || '',
    context: [],
  }

  // Remove any existing markers
  if (markerRef.value) {
    markerRef.value.remove()
  }

  // Add a marker to the map
  markerRef.value = new mapboxgl.Marker().setLngLat([placeLocation.center[0], placeLocation.center[1]]).addTo(mapbox)

  mapbox.flyTo({
    zoom: 13,
    center: [placeLocation.center[0], placeLocation.center[1]],
  })
}

// GeoJSON object to hold our measurement features
const geojson: any = {
  type: 'FeatureCollection',
  features: [],
}

// Used to draw a line between points
const linestring: any = {
  type: 'Feature',
  geometry: {
    type: 'LineString',
    coordinates: [],
  },
}

/**
 * Define interactions here.
 */
const initInteractions = () => {
  mapbox.on('load', () => {
    mapbox.addSource('geojson', {
      type: 'geojson',
      data: geojson,
    })

    // Add styles to the map
    mapbox.addLayer({
      id: 'measure-points',
      type: 'circle',
      source: 'geojson',
      paint: {
        'circle-radius': 7,
        'circle-color': '#000',
      },
      filter: ['in', '$type', 'Point'],
    })

    mapbox.addLayer({
      id: 'measure-lines',
      type: 'line',
      source: 'geojson',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 2.5,
        'line-dasharray': [2, 2],
      },
      filter: ['in', '$type', 'LineString'],
    })

    mapbox.on('click', (e) => {
      const features: any = mapbox.queryRenderedFeatures(e.point, {
        layers: ['measure-points'],
      })

      // Remove the linestring from the group
      // so we can redraw it based on the points collection.
      if (geojson.features.length > 1) geojson.features.pop()

      // If a feature was clicked, remove it from the map.
      if (features.length) {
        const id = features[0].properties.id
        geojson.features = geojson.features.filter((point: any) => point.properties.id !== id)
      } else {
        const point = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [e.lngLat.lng, e.lngLat.lat],
          },
          properties: {
            id: String(new Date().getTime()),
          },
        }

        geojson.features.push(point)
      }

      if (geojson.features.length > 1) {
        linestring.geometry.coordinates = geojson.features.map((point: any) => point.geometry.coordinates)

        geojson.features.push(linestring)

        // Populate the distanceContainer with total distance
        const distance = roundTo(turf.length(linestring))
        emit('update:distance', distance)
      }

      //@ts-ignore
      mapbox.getSource('geojson').setData(geojson)
    })
  })

  mapbox.on('mousemove', (e) => {
    const features = mapbox.queryRenderedFeatures(e.point, {
      layers: ['measure-points'],
    })
    // Change the cursor to a pointer when hovering over a point on the map.
    // Otherwise cursor is a crosshair.
    mapbox.getCanvas().style.cursor = features.length ? 'pointer' : 'crosshair'
  })
}

const initializeMapBox = () => {
  mapbox = new mapboxgl.Map({
    container: props.container,
    style: props.style,
    //center near africa
    center: [10, 10],
    maxZoom: 17,
    minZoom: 1.2,
    projection: props.projectionType,
    doubleClickZoom: true,
  })

  if (!mapbox) {
    console.error('Mapbox is not initialized')
    return
  }

  //Add navigation controls
  mapbox.addControl(
    new mapboxgl.NavigationControl({
      showZoom: true,
    }),
    'top-right',
  )

  mapbox.addControl(new mapboxgl.FullscreenControl(), 'top-right')

  initInteractions()
}

const clearQuery = () => {
  query.value = ''
}

const clearRoute = () => {
  geojson.features = []
  //@ts-ignore
  mapbox.getSource('geojson').setData(geojson)
  emit('clear:complete')
}

onMounted(() => {
  mapboxgl.accessToken = props.accessToken
  initializeMapBox()
})
</script>
<template>
  <div class="bg-[rgb(120_188_237)] flex flex-col relative">
    <div class="absolute z-10 top-2 left-2 flex items-center max-w-sm mx-auto">
      <label for="simple-search" class="sr-only">Search</label>
      <div class="relative w-full">
        <input
          type="text"
          id="simple-search"
          class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full"
          placeholder="Search for a location"
          v-model="query"
          @keyup.enter="searchLocationFromPlaces"
        />
        <!-- svg for x icon -->
        <button @click="clearQuery" class="absolute end-2 top-1/2 -translate-y-1/2 text-gray-500">
          <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <button
        class="p-2.5 ms-1 text-sm font-medium text-white bg-blue-700 rounded-lg border border-blue-700 hover:bg-blue-800"
        @click.stop="searchLocationFromPlaces"
      >
        <svg class="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
          />
        </svg>
        <span class="sr-only">Search</span>
      </button>
    </div>

    <div :id="container" class="w-full h-[400px] md:h-[450px]"></div>
    <button
      @click="clearRoute"
      class="absolute bottom-2 left-2 z-20 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
    >
      Clear Route
    </button>
  </div>
</template>
